import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  addMonths,
  subMonths,
  isSameMonth,
  format,
} from "date-fns";
import api, { formatApiError } from "@/lib/api";
import { fmtMins, recurrenceSummary, WEEKDAYS, todayNZ, ymd } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Plus,
  Trash2,
  Loader2,
  CalendarDays,
  ListChecks,
  Users,
  UserCog,
  Repeat,
  ChevronLeft,
  ChevronRight,
  Clock,
  Sparkles,
  KeyRound,
} from "lucide-react";

const REC_TYPES = [
  { v: "daily", label: "Every day" },
  { v: "every_n_days", label: "Every N days" },
  { v: "weekdays", label: "Weekly (specific days)" },
  { v: "day_of_month", label: "Monthly (day of month)" },
  { v: "every_n_months", label: "Every N months" },
  { v: "once", label: "One-off (single date)" },
];

function emptyForm() {
  return {
    name: "",
    description: "",
    assignee_type: "cleaners",
    est_minutes: 15,
    recurrence: {
      type: "weekdays",
      interval: 2,
      weekdays: [0],
      day_of_month: 1,
      date: todayNZ(),
      start_date: todayNZ(),
    },
  };
}

export default function Tasks() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);

  const { data: tasks = [], isLoading } = useQuery({
    queryKey: ["task-defs"],
    queryFn: async () => (await api.get("/task-defs")).data,
  });

  const del = useMutation({
    mutationFn: (id) => api.delete(`/task-defs/${id}`),
    onSuccess: () => {
      toast.success(t("Task deleted"));
      qc.invalidateQueries({ queryKey: ["task-defs"] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const cleanerTasks = tasks.filter((t) => t.assignee_type === "cleaners");
  const managerTasks = tasks.filter((t) => t.assignee_type === "manager");

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-extrabold">{t("Scheduled Tasks")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("Recurring jobs that appear automatically on their due days — independent of the daily CSV.")}
          </p>
        </div>
        <Button
          onClick={() => setEditing({ new: true, ...emptyForm() })}
          data-testid="add-task-btn"
          className="k-press gap-2"
        >
          <Plus className="h-4 w-4" /> {t("New Task")}
        </Button>
      </div>

      <AutoKeyReturnConfig />
      <RefillTrolleyConfig />

      <Tabs defaultValue="list">
        <TabsList data-testid="tasks-tabs">
          <TabsTrigger value="list" data-testid="tab-list" className="gap-2">
            <ListChecks className="h-4 w-4" /> {t("Tasks")}
          </TabsTrigger>
          <TabsTrigger value="calendar" data-testid="tab-calendar" className="gap-2">
            <CalendarDays className="h-4 w-4" /> {t("Calendar")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="list" className="mt-5">
          <div className="max-h-[calc(100vh-230px)] space-y-6 overflow-y-auto pr-1" data-testid="tasks-scroll">
          {isLoading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : tasks.length === 0 ? (
            <Card className="flex flex-col items-center gap-3 rounded-md border border-dashed py-20 text-center">
              <Repeat className="h-8 w-8 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                {t('No scheduled tasks yet. Create one like "Clean Gym" or "Evacuation trial".')}
              </p>
            </Card>
          ) : (
            <>
              <TaskGroup
                title={t("Cleaner tasks")}
                icon={Users}
                tasks={cleanerTasks}
                onEdit={setEditing}
                onDelete={del.mutate}
              />
              <TaskGroup
                title={t("Manager tasks (to-do)")}
                icon={UserCog}
                tasks={managerTasks}
                onEdit={setEditing}
                onDelete={del.mutate}
              />
            </>
          )}
          </div>
        </TabsContent>

        <TabsContent value="calendar" className="mt-5">
          <CalendarView onAddOnDate={(d) => setEditing({ new: true, ...onceForm(d) })} />
        </TabsContent>
      </Tabs>

      {editing && (
        <TaskEditor
          task={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            qc.invalidateQueries({ queryKey: ["task-defs"] });
          }}
        />
      )}
    </div>
  );
}

function onceForm(dateStr) {
  const f = emptyForm();
  f.recurrence.type = "once";
  f.recurrence.date = dateStr;
  return f;
}

function AutoKeyReturnConfig() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["auto-key-return-config"],
    queryFn: async () => (await api.get("/auto-key-return/config")).data,
  });
  const [enabled, setEnabled] = useState(null);
  const [time, setTime] = useState("10:00");
  useEffect(() => {
    if (data) {
      setEnabled(!!data.enabled);
      setTime(data.time || "10:00");
    }
  }, [data]);

  const save = useMutation({
    mutationFn: () => api.put("/auto-key-return/config", { enabled, time }),
    onSuccess: () => {
      toast.success("Auto key-return updated");
      qc.invalidateQueries({ queryKey: ["auto-key-return-config"] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  if (isLoading || enabled === null) return null;

  return (
    <Card className="space-y-4 rounded-md border p-4" data-testid="auto-key-return-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          <KeyRound className="mt-0.5 h-5 w-5 text-sky-600" />
          <div>
            <div className="font-display text-lg font-extrabold">{t("Auto mark keys returned")}</div>
            <p className="text-xs text-muted-foreground">
              {t("Automatically mark every departure room's key as returned at a set time each day, so stripping can begin without waiting on a manual tap. You can still Undo a room before it's stripped.")}
            </p>
          </div>
        </div>
        <Switch
          checked={enabled}
          onCheckedChange={setEnabled}
          data-testid="auto-key-return-toggle"
        />
      </div>

      {enabled && (
        <div className="flex flex-wrap items-end gap-3" data-testid="auto-key-return-time-row">
          <div className="space-y-1.5">
            <Label htmlFor="auto-key-return-time">{t("Mark off at (NZ time)")}</Label>
            <Input
              id="auto-key-return-time"
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              data-testid="auto-key-return-time"
              className="w-36"
            />
          </div>
          <p className="text-xs text-muted-foreground">{t("Applies to that day's departure rooms only.")}</p>
        </div>
      )}

      <div className="flex justify-end">
        <Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="auto-key-return-save" className="gap-2">
          {save.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          {t("Save")}
        </Button>
      </div>
    </Card>
  );
}


function RefillTrolleyConfig() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => (await api.get("/settings")).data,
  });
  const save = useMutation({
    mutationFn: (enabled) => api.put("/settings", { refill_trolley_enabled: enabled }),
    onSuccess: (_r, enabled) => {
      toast.success(enabled ? t("Refill Trolley task on") : t("Refill Trolley task off"));
      qc.invalidateQueries({ queryKey: ["settings"] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  if (isLoading || !data) return null;
  const enabled = data.refill_trolley_enabled !== false;

  return (
    <Card className="space-y-2 rounded-md border p-4" data-testid="refill-trolley-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          <Sparkles className="mt-0.5 h-5 w-5 text-amber-600" />
          <div>
            <div className="font-display text-lg font-extrabold">{t("End-of-day trolley refill")}</div>
            <p className="text-xs text-muted-foreground">
              {t('Give each cleaner a private "Refill Trolley - Solutions, Consumables" task the first time they start a job that day. Only that cleaner can see and complete their own — it can\'t be joined by anyone else.')}
            </p>
          </div>
        </div>
        <Switch
          checked={enabled}
          onCheckedChange={(v) => save.mutate(v)}
          disabled={save.isPending}
          data-testid="refill-trolley-toggle"
        />
      </div>
    </Card>
  );
}


function TaskGroup({ title, icon: Icon, tasks, onEdit, onDelete }) {
  const { t: tr } = useTranslation();
  if (tasks.length === 0) return null;
  return (
    <div className="space-y-2.5">
      <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
        <Icon className="h-4 w-4" /> {title} ({tasks.length})
      </div>
      <div className="space-y-2" data-testid={`task-group-${title}`}>
        {tasks.map((t) => (
          <Card
            key={t.id}
            data-testid={`task-card-${t.name}`}
            className="k-press flex cursor-pointer items-start gap-3 rounded-md border px-4 py-3 hover:border-primary/40"
            onClick={() => onEdit(t)}
          >
            <div className="min-w-0 flex-1">
              <div className="truncate font-display text-base font-extrabold">{t.name}</div>
              {t.description && (
                <div className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">{t.description}</div>
              )}
              <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs font-semibold text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  <Repeat className="h-3 w-3" /> {recurrenceSummary(t.recurrence)}
                </span>
                <span className="inline-flex items-center gap-1">
                  <Clock className="h-3 w-3" /> {fmtMins(t.est_minutes)}
                </span>
                {t.assignee_type === "manager" && t.next_due_date && (
                  <span>{tr("Due {{date}}", { date: t.next_due_date })}</span>
                )}
              </div>
            </div>
            <button
              type="button"
              data-testid={`delete-task-${t.name}`}
              onClick={(e) => {
                e.stopPropagation();
                onDelete(t.id);
              }}
              className="k-press shrink-0 rounded-md p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </Card>
        ))}
      </div>
    </div>
  );
}

export function TaskEditor({ task, onClose, onSaved }) {
  const { t } = useTranslation();
  const [name, setName] = useState(task.name || "");
  const [description, setDescription] = useState(task.description || "");
  const [assignee, setAssignee] = useState(task.assignee_type || "cleaners");
  const [est, setEst] = useState(task.est_minutes ?? 15);
  const [rec, setRec] = useState({ ...emptyForm().recurrence, ...task.recurrence });
  const [managerIds, setManagerIds] = useState(task.manager_ids || []);
  const [roomChecklist, setRoomChecklist] = useState(task.room_checklist || []);

  const { data: managers = [] } = useQuery({
    queryKey: ["managers"],
    queryFn: async () => (await api.get("/managers")).data,
  });
  const { data: rooms = [] } = useQuery({
    queryKey: ["rooms"],
    queryFn: async () => (await api.get("/rooms")).data,
  });
  const activeRooms = rooms
    .filter((r) => r.active !== false)
    .sort((a, b) => String(a.room_number).localeCompare(String(b.room_number), undefined, { numeric: true }));
  const toggleManager = (id) =>
    setManagerIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const toggleRoom = (rn) =>
    setRoomChecklist((prev) => (prev.includes(rn) ? prev.filter((x) => x !== rn) : [...prev, rn]));

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        name,
        description,
        assignee_type: assignee,
        est_minutes: Number(est) || 0,
        recurrence: rec,
        manager_ids: assignee === "manager" ? managerIds : [],
        room_checklist: assignee === "manager" ? roomChecklist : [],
      };
      return task.new
        ? api.post("/task-defs", payload)
        : api.put(`/task-defs/${task.id}`, payload);
    },
    onSuccess: () => {
      toast.success(t("Task saved"));
      onSaved();
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const setRecField = (patch) => setRec((r) => ({ ...r, ...patch }));
  const toggleWeekday = (d) =>
    setRec((r) => {
      const has = (r.weekdays || []).includes(d);
      return { ...r, weekdays: has ? r.weekdays.filter((x) => x !== d) : [...(r.weekdays || []), d].sort() };
    });

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="task-editor-dialog">
        <DialogHeader>
          <DialogTitle>{task.new ? t("New scheduled task") : t("Edit {{name}}", { name: task.name })}</DialogTitle>
          <DialogDescription>
            {t("Set who does it and how often. It appears automatically on its due days.")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>{t("Task name")}</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("e.g. Clean Gym, Evacuation trial")}
              data-testid="task-name-input"
            />
          </div>

          <div className="space-y-1.5">
            <Label>{t("Description (optional)")}</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t("Any notes for whoever does this task")}
              rows={2}
              data-testid="task-description-input"
            />
          </div>

          <div className="space-y-1.5">
            <Label>{t("Who does this task?")}</Label>
            <RadioGroup
              value={assignee}
              onValueChange={setAssignee}
              className="grid grid-cols-2 gap-2"
              data-testid="task-assignee-group"
            >
              <label
                className={`k-press flex cursor-pointer items-center gap-2 rounded-md border p-3 text-sm font-medium ${
                  assignee === "cleaners" ? "border-primary bg-primary/5" : "border-border"
                }`}
              >
                <RadioGroupItem value="cleaners" data-testid="assignee-cleaners" />
                <Users className="h-4 w-4" /> {t("All cleaners")}
              </label>
              <label
                className={`k-press flex cursor-pointer items-center gap-2 rounded-md border p-3 text-sm font-medium ${
                  assignee === "manager" ? "border-primary bg-primary/5" : "border-border"
                }`}
              >
                <RadioGroupItem value="manager" data-testid="assignee-manager" />
                <UserCog className="h-4 w-4" /> {t("Manager (to-do)")}
              </label>
            </RadioGroup>
          </div>

          {assignee === "manager" && (
            <div className="space-y-1.5" data-testid="manager-assign-picker">
              <Label>{t("Assign to managers")}</Label>
              <p className="text-xs text-muted-foreground">
                {t("Leave all unticked for all managers. Everyone sees every task; it's grouped under each assigned manager in My To-Do.")}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {managers.map((m) => {
                  const on = managerIds.includes(m.id);
                  return (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => toggleManager(m.id)}
                      data-testid={`assign-manager-${m.id}`}
                      className={`k-press rounded-md border px-3 py-1.5 text-sm font-semibold ${
                        on ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:bg-secondary"
                      }`}
                    >
                      {m.name}
                    </button>
                  );
                })}
              </div>
              <div className="text-xs font-medium text-muted-foreground">
                {managerIds.length === 0 ? t("→ All managers") : t("→ {{count}} manager(s)", { count: managerIds.length })}
              </div>
            </div>
          )}

          {assignee === "manager" && (
            <div className="space-y-1.5 rounded-md border border-border p-3" data-testid="room-checklist-editor">
              <label className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold">{t("Room-by-room checklist")}</div>
                  <div className="text-xs text-muted-foreground">
                    {t("For tasks done over several days (e.g. deep-clean carpets). Tick rooms off as you go — progress is saved until you mark the whole task done.")}
                  </div>
                </div>
                <Switch
                  checked={roomChecklist.length > 0}
                  onCheckedChange={(on) => setRoomChecklist(on ? activeRooms.map((r) => r.room_number) : [])}
                  data-testid="room-checklist-toggle"
                />
              </label>
              {roomChecklist.length > 0 && (
                <div className="space-y-2 pt-1">
                  <div className="flex items-center gap-2 text-xs">
                    <button type="button" onClick={() => setRoomChecklist(activeRooms.map((r) => r.room_number))} className="font-semibold text-primary hover:underline" data-testid="room-checklist-all">
                      {t("Select all")}
                    </button>
                    <span className="text-muted-foreground">·</span>
                    <button type="button" onClick={() => setRoomChecklist([])} className="font-semibold text-muted-foreground hover:underline" data-testid="room-checklist-none">
                      {t("Clear")}
                    </button>
                    <span className="ml-auto font-medium text-muted-foreground">{t("{{count}} selected", { count: roomChecklist.length })}</span>
                  </div>
                  <div className="flex max-h-44 flex-wrap gap-1.5 overflow-y-auto">
                    {activeRooms.map((r) => {
                      const on = roomChecklist.includes(r.room_number);
                      return (
                        <button
                          key={r.id || r.room_number}
                          type="button"
                          onClick={() => toggleRoom(r.room_number)}
                          data-testid={`room-pick-${r.room_number}`}
                          className={`k-press rounded-md border px-2.5 py-1 text-sm font-semibold ${
                            on ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:bg-secondary"
                          }`}
                        >
                          {r.room_number}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t("Recurrence")}</Label>
              <Select value={rec.type} onValueChange={(v) => setRecField({ type: v })}>
                <SelectTrigger data-testid="recurrence-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REC_TYPES.map((r) => (
                    <SelectItem key={r.v} value={r.v} data-testid={`rec-type-${r.v}`}>
                      {t(r.label)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("Est. minutes")}</Label>
              <Input
                type="number"
                min="0"
                value={est}
                onChange={(e) => setEst(e.target.value)}
                data-testid="task-est-input"
              />
            </div>
          </div>

          {(rec.type === "every_n_days" || rec.type === "every_n_months") && (
            <div className="space-y-1.5">
              <Label>{rec.type === "every_n_days" ? t("Every how many days?") : t("Every how many months?")}</Label>
              <Input
                type="number"
                min="1"
                value={rec.interval}
                onChange={(e) => setRecField({ interval: Number(e.target.value) || 1 })}
                data-testid="recurrence-interval-input"
              />
            </div>
          )}

          {rec.type === "weekdays" && (
            <div className="space-y-1.5">
              <Label>{t("On which days?")}</Label>
              <div className="flex flex-wrap gap-2" data-testid="weekday-picker">
                {WEEKDAYS.map((d, i) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => toggleWeekday(i)}
                    data-testid={`weekday-${i}`}
                    className={`k-press h-9 w-12 rounded-md border text-sm font-semibold ${
                      (rec.weekdays || []).includes(i)
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border bg-secondary/50 hover:bg-secondary"
                    }`}
                  >
                    {d}
                  </button>
                ))}
              </div>
            </div>
          )}

          {(rec.type === "day_of_month" || rec.type === "every_n_months") && (
            <div className="space-y-1.5">
              <Label>{t("Day of the month")}</Label>
              <Input
                type="number"
                min="1"
                max="31"
                value={rec.day_of_month || 1}
                onChange={(e) => setRecField({ day_of_month: Number(e.target.value) || 1 })}
                data-testid="day-of-month-input"
              />
            </div>
          )}

          {rec.type === "once" ? (
            <div className="space-y-1.5">
              <Label>{t("Date")}</Label>
              <Input
                type="date"
                value={rec.date || todayNZ()}
                onChange={(e) => setRecField({ date: e.target.value })}
                data-testid="once-date-input"
              />
            </div>
          ) : (
            <div className="space-y-1.5">
              <Label>{t("Starts on")}</Label>
              <Input
                type="date"
                value={rec.start_date || todayNZ()}
                onChange={(e) => setRecField({ start_date: e.target.value })}
                data-testid="start-date-input"
              />
            </div>
          )}

          <div className="rounded-md border border-border bg-secondary/40 px-3 py-2 text-sm">
            <span className="label-caps text-muted-foreground">{t("Summary")}</span>
            <div className="mt-0.5 font-medium">{recurrenceSummary(rec)}</div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Button
            onClick={() => save.mutate()}
            disabled={!name || save.isPending}
            data-testid="save-task-btn"
            className="gap-2"
          >
            {save.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {t("Save task")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CalendarView({ onAddOnDate }) {
  const { t } = useTranslation();
  const [month, setMonth] = useState(() => startOfMonth(new Date()));
  const [audience, setAudience] = useState("all");

  const gridStart = startOfWeek(startOfMonth(month), { weekStartsOn: 1 });
  const gridEnd = endOfWeek(endOfMonth(month), { weekStartsOn: 1 });
  const days = eachDayOfInterval({ start: gridStart, end: gridEnd });

  const { data: occ = [] } = useQuery({
    queryKey: ["calendar", ymd(gridStart), ymd(gridEnd), audience],
    queryFn: async () =>
      (await api.get(`/calendar?start=${ymd(gridStart)}&end=${ymd(gridEnd)}&audience=${audience}`)).data,
  });

  const byDate = {};
  for (const o of occ) (byDate[o.date] = byDate[o.date] || []).push(o);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setMonth(subMonths(month, 1))} data-testid="cal-prev">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-[160px] text-center font-display text-lg font-extrabold" data-testid="cal-month-label">
            {format(month, "MMMM yyyy")}
          </div>
          <Button variant="outline" size="icon" onClick={() => setMonth(addMonths(month, 1))} data-testid="cal-next">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="inline-flex rounded-md border border-border bg-card p-1" data-testid="calendar-audience-filter">
          {[
            { k: "all", label: t("Both") },
            { k: "cleaners", label: t("Cleaner") },
            { k: "manager", label: t("Manager") },
          ].map((f) => (
            <button
              key={f.k}
              onClick={() => setAudience(f.k)}
              data-testid={`cal-filter-${f.k}`}
              className={`k-press rounded px-3 py-1.5 text-sm font-medium ${
                audience === f.k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        {t("Shows non-daily scheduled tasks only. Click a day to add a one-off task.")}
      </p>

      <div className="grid grid-cols-7 gap-px overflow-hidden rounded-md border border-border bg-border">
        {WEEKDAYS.map((d) => (
          <div key={d} className="bg-card py-2 text-center text-xs font-semibold text-muted-foreground">
            {d}
          </div>
        ))}
        {days.map((d) => {
          const ds = ymd(d);
          const items = byDate[ds] || [];
          const inMonth = isSameMonth(d, month);
          return (
            <button
              key={ds}
              onClick={() => onAddOnDate(ds)}
              data-testid={`cal-day-${ds}`}
              className={`min-h-[92px] bg-card p-1.5 text-left align-top transition-colors hover:bg-secondary/60 ${
                inMonth ? "" : "opacity-40"
              }`}
            >
              <div className="mb-1 text-xs font-semibold text-muted-foreground">{format(d, "d")}</div>
              <div className="space-y-1">
                {items.slice(0, 3).map((o, idx) => (
                  <div
                    key={`${o.task_def_id || "occ"}-${idx}`}
                    className={`truncate rounded px-1.5 py-0.5 text-[11px] font-medium ${
                      o.assignee_type === "manager"
                        ? "bg-amber-100 text-amber-800"
                        : "bg-blue-100 text-blue-800"
                    }`}
                  >
                    {o.name}
                  </div>
                ))}
                {items.length > 3 && (
                  <div className="text-[10px] text-muted-foreground">{t("+{{count}} more", { count: items.length - 3 })}</div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
